import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2 as DeleteIcon, Pause as PauseIcon, Play as PlayArrowIcon, Info as InfoOutlinedIcon, SlidersHorizontal as TuneOutlinedIcon, CircleCheckBig as CompleteIcon } from 'lucide-react';
import { Button as UiButton, Chip, ShellHeader, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { useSessionStore } from '../store/sessionStore';
import { MessageListWithStreaming } from '../components/sessions/MessageListWithStreaming';
import { MessageInput } from '../components/sessions/MessageInput';
import { ContextUsageIndicator } from '../components/sessions/ContextUsageIndicator';
import { ContextSummaryList } from '../components/sessions/ContextSummaryList';
import { SessionMemoryList } from '../components/sessions/SessionMemoryList';
import { SessionMemoryScope } from '../components/memory/SessionMemoryScope';
import { SessionMemoryOutcome } from '../components/memory/SessionMemoryOutcome';
import { SessionMemoryDecisions } from '../components/memory/SessionMemoryDecisions';
import { FileUpload } from '../components/files/FileUpload';
import { FileList } from '../components/files/FileList';
import { HarnessMode, MessageRole, SessionStatus, parseToolCalls, type HarnessTodoItem } from '../types/session';
import { FileCategory } from '../types/file';
import { fileService } from '../services/fileService';
import { agentService } from '../services/agentService';
import { signalRService } from '../services/signalRService';
import { useNotificationStore } from '../store/notificationStore';
import { useConfirmDialog } from '../components/common/ConfirmDialog';

/**
 * Session detail page with chat interface.
 */
export const SessionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const {
    currentSession,
    messages,
    loading,
    busy,
    error,
    loadSession,
    loadMessages,
    sendMessage,
    deleteSession,
    pauseSession,
    resumeSession,
    completeSession,
    clearError,
  } = useSessionStore();
  const { showNotification } = useNotificationStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [parameterLabelsByKey, setParameterLabelsByKey] = useState<Record<string, string>>({});
  const [agentUsesHarness, setAgentUsesHarness] = useState(false);
  const [harnessMode, setHarnessMode] = useState<HarnessMode>(HarnessMode.Execute);
  const [harnessTodosJson, setHarnessTodosJson] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadSession(id);
      loadMessages(id);
    }
  }, [id, loadSession, loadMessages]);

  useEffect(() => {
    const defId = currentSession?.agentDefinitionId;
    if (!defId) {
      setParameterLabelsByKey({});
      return;
    }
    let cancelled = false;
    void agentService
      .getAgent(defId)
      .then((agent) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const p of agent.parameters) {
          map[p.key] = p.label;
        }
        setParameterLabelsByKey(map);
        setAgentUsesHarness(agent.useHarnessAgent ?? false);
      })
      .catch(() => {
        if (!cancelled) {
          setParameterLabelsByKey({});
          setAgentUsesHarness(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentSession?.agentDefinitionId]);

  useEffect(() => {
    if (currentSession?.currentHarnessMode) {
      setHarnessMode(currentSession.currentHarnessMode as HarnessMode);
    }
    setHarnessTodosJson(currentSession?.harnessTodosJson ?? null);
  }, [currentSession?.currentHarnessMode, currentSession?.harnessTodosJson]);

  useEffect(() => {
    signalRService.on({
      onHarnessStateUpdated: (data) => {
        if (data.sessionId !== id) return;
        setHarnessMode(data.mode as HarnessMode);
        setHarnessTodosJson(data.todosJson ?? null);
      },
    });
    return () => {
      signalRService.on({ onHarnessStateUpdated: undefined });
    };
  }, [id]);

  const harnessTodos = useMemo<HarnessTodoItem[]>(() => {
    if (!harnessTodosJson) return [];
    try {
      const parsed = JSON.parse(harnessTodosJson);
      return Array.isArray(parsed) ? (parsed as HarnessTodoItem[]) : [];
    } catch {
      return [];
    }
  }, [harnessTodosJson]);

  const handleStreamingComplete = useCallback(() => {
    if (id) {
      loadMessages(id);
    }
  }, [id, loadMessages]);

  const parsedParameters = useMemo(() => {
    if (!currentSession?.initialParametersJson) return null;
    try {
      const params = JSON.parse(currentSession.initialParametersJson) as Record<string, unknown>;
      const entries = Object.entries(params);
      return entries.length > 0 ? entries : null;
    } catch {
      return null;
    }
  }, [currentSession?.initialParametersJson]);

  const handleSendMessage = async (payload: { content: string; file?: File; mediaType?: string }) => {
    if (!id) return;

    try {
      let fileId: string | undefined;

      if (payload.file) {
        const uploaded = await fileService.uploadFile(id, payload.file, FileCategory.Attachment);
        fileId = uploaded.id;
        setFilesRefreshKey((k) => k + 1);
      }

      await sendMessage(id, {
        content: payload.content,
        fileId,
        mediaType: payload.mediaType,
        harnessMode: agentUsesHarness ? harnessMode : undefined,
      });
    } catch (err: any) {
      showNotification(err.message || 'Failed to send message', 'error');
    }
  };

  const handleDelete = async () => {
    if (!id || !currentSession) return;

    const ok = await confirm({
      title: 'Delete session',
      message: 'Are you sure you want to delete this session?',
      confirmText: 'Delete',
    });
    if (!ok) return;

    try {
      await deleteSession(id);
      showNotification('Session deleted successfully', 'success');
      navigate('/sessions');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete session', 'error');
    }
  };

  const handlePause = async () => {
    if (!id) return;

    try {
      await pauseSession(id);
      showNotification('Session paused', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to pause session', 'error');
    }
  };

  const handleResume = async () => {
    if (!id) return;

    try {
      await resumeSession(id);
      showNotification('Session resumed', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to resume session', 'error');
    }
  };

  const handleComplete = async () => {
    if (!id) return;

    const ok = await confirm({
      title: 'Complete session',
      message:
        'Completing marks this session finished and queues it for memory extraction (when the agent has a memory definition). Continue?',
      confirmText: 'Complete',
    });
    if (!ok) return;

    try {
      const updated = await completeSession(id);
      switch (updated.memoryQueueStatus) {
        case 'Queued':
          showNotification('Session completed — queued for memory extraction', 'success');
          break;
        case 'Duplicate':
          showNotification('Session completed — already queued for memory extraction', 'success');
          break;
        case 'Ignored':
          showNotification(
            'Session completed — no memory definition, not queued (activate under Memory Definitions if Draft)',
            'info'
          );
          break;
        case 'SkippedScope':
          showNotification(
            `Session completed — not queued for memory: ${updated.memoryQueueReason || 'scope missing'}`,
            'warning'
          );
          break;
        case 'Error':
          showNotification('Session completed — memory enqueue failed (see server logs)', 'warning');
          break;
        default:
          showNotification('Session completed', 'success');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to complete session', 'error');
    }
  };

  const getStatusColor = (status: SessionStatus): 'green' | 'amber' | 'blue' | 'red' | 'default' => {
    switch (status) {
      case SessionStatus.Active:
        return 'green';
      case SessionStatus.Paused:
        return 'amber';
      case SessionStatus.Completed:
        return 'blue';
      default:
        return 'red';
    }
  };

  const getStatusLabel = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.Active:
        return 'Active';
      case SessionStatus.Paused:
        return 'Paused';
      case SessionStatus.Completed:
        return 'Completed';
      case SessionStatus.Cancelled:
        return 'Cancelled';
      case SessionStatus.Expired:
        return 'Expired';
      case SessionStatus.Error:
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  if (!currentSession) {
    return (
      <div className="session-detail" data-testid="session-detail-page">
        <ShellHeader
          title={loading ? 'Session' : 'Session'}
          description={loading ? 'Loading…' : 'Session not found.'}
          actions={
            !loading ? (
              <UiButton type="button" variant="outline" size="sm" onClick={() => navigate('/sessions')} data-testid="back-session-btn">
                Back
              </UiButton>
            ) : undefined
          }
        />
        <PageContainer>
          {loading ? (
            <LoadingSkeleton rows={6} data-testid="session-detail-loading" />
          ) : (
            <InlineErrorPanel message="Session not found" data-testid="session-detail-error" />
          )}
        </PageContainer>
      </div>
    );
  }

  const sessionMessages = messages.get(id || '') || [];

  const toolActivity = sessionMessages
    .filter((m) => m.role === MessageRole.Assistant)
    .flatMap((m) =>
      parseToolCalls(m.toolCallsJson).map((call, index) => {
        let status: 'completed' | 'failed' | 'called' = 'called';
        if (call.success === true) {
          status = 'completed';
        } else if (call.success === false) {
          status = 'failed';
        } else if (call.id) {
          const toolMessage = sessionMessages.find((t) => {
            if (t.role !== MessageRole.Tool || !t.toolResultsJson) return false;
            try {
              const meta = JSON.parse(t.toolResultsJson) as { toolCallId?: string };
              return meta.toolCallId === call.id;
            } catch {
              return false;
            }
          });
          if (toolMessage) {
            status = toolMessage.content?.startsWith('Error:') ? 'failed' : 'completed';
          }
        }
        return {
          key: `${m.id}-${call.id || index}`,
          name: call.name,
          status,
          timestamp: m.createdAt,
        };
      }),
    );

  return (
    <div className="session-detail flex flex-col h-full overflow-hidden" data-testid="session-detail-page">
      <ShellHeader
        title={currentSession.sessionName}
        description={currentSession.agentName}
        actions={
          <div className="flex items-center gap-2">
            <TooltipProvider>
              {agentUsesHarness && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Chip className="text-xs" variant="outlined" color="violet" label={`Mode: ${harnessMode.charAt(0).toUpperCase()}${harnessMode.slice(1)}`} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Current harness operating mode</TooltipContent>
                </Tooltip>
              )}
              <Chip className="text-xs" color={getStatusColor(currentSession.status)} data-testid="session-status" label={getStatusLabel(currentSession.status)} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <UiButton type="button" variant="ghost" size="icon" onClick={() => setSidebarOpen((v) => !v)} data-testid="session-link-context">
                    <InfoOutlinedIcon />
                  </UiButton>
                </TooltipTrigger>
                <TooltipContent>Context details</TooltipContent>
              </Tooltip>
              {currentSession.status === SessionStatus.Active ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <UiButton type="button" variant="ghost" size="icon" className="text-amber-500" onClick={handlePause} data-testid="pause-session-btn">
                      <PauseIcon />
                    </UiButton>
                  </TooltipTrigger>
                  <TooltipContent>Pause</TooltipContent>
                </Tooltip>
              ) : currentSession.status === SessionStatus.Paused ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <UiButton type="button" variant="ghost" size="icon" className="text-green-500" onClick={handleResume} data-testid="resume-session-btn">
                      <PlayArrowIcon />
                    </UiButton>
                  </TooltipTrigger>
                  <TooltipContent>Resume</TooltipContent>
                </Tooltip>
              ) : null}
              {(currentSession.status === SessionStatus.Active ||
                currentSession.status === SessionStatus.Paused) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <UiButton type="button" variant="ghost" size="icon" className="text-green-500" onClick={handleComplete} data-testid="complete-session-btn">
                      <CompleteIcon />
                    </UiButton>
                  </TooltipTrigger>
                  <TooltipContent>Complete — marks the session finished and queues it for memory extraction</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <UiButton type="button" variant="ghost" size="icon" className="text-red-500" onClick={handleDelete} data-testid="delete-session-btn">
                    <DeleteIcon />
                  </UiButton>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <UiButton type="button" variant="outline" size="sm" onClick={() => navigate('/sessions')} data-testid="back-session-btn">
              Back
            </UiButton>
          </div>
        }
      />
    <div className="flex flex-1 flex-col min-h-0">

      <div className="flex-shrink-0">
        <SessionMemoryScope
          session={currentSession}
          onSaved={() => id && loadSession(id)}
        />
        {id && <SessionMemoryOutcome sessionId={id} />}
        {id && <SessionMemoryDecisions sessionId={id} />}
      </div>

      {/* Main Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Chat Area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {error && (
            <div className="m-3">
              <InlineErrorPanel
                message={error}
                action={<UiButton type="button" variant="ghost" size="sm" onClick={clearError}>Dismiss</UiButton>}
                data-testid="session-detail-error"
              />
            </div>
          )}

          <MessageListWithStreaming
            messages={sessionMessages}
            sessionId={id || ''}
            loading={!messages.has(id || '')}
            onStreamingComplete={handleStreamingComplete}
          />

          <MessageInput
            onSend={handleSendMessage}
            disabled={currentSession.status !== SessionStatus.Active || busy}
            placeholder={
              currentSession.status !== SessionStatus.Active
                ? 'Session is not active'
                : 'Type your message...'
            }
            modePicker={
              agentUsesHarness
                ? { mode: harnessMode, onChange: setHarnessMode }
                : undefined
            }
          />
        </div>

        {/* Sidebar — replaces MUI Drawer */}
        <div
          className={`flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-80' : 'w-0'}`}
        >
          <div className="h-full w-80 overflow-y-auto border-l border-[var(--color-border-default)]">
            <div className="p-6" data-testid="session-section-details">
              <h6 className="text-base font-semibold mb-1">Session Details</h6>
              <hr className="border-[var(--color-border-default)] my-6" />

              <div className="mb-6">
                <ContextUsageIndicator sessionId={currentSession.id} />
              </div>
              <hr className="border-[var(--color-border-default)] my-6" />

              <ul className="space-y-3">
                <li className="flex flex-col">
                  <span className="text-sm font-medium">Messages</span>
                  <span className="text-sm text-[var(--color-text-secondary)]">{currentSession.messageCount}</span>
                </li>
                <li className="flex flex-col">
                  <span className="text-sm font-medium">Tokens Used</span>
                  <span className="text-sm text-[var(--color-text-secondary)]">{currentSession.totalTokensUsed.toLocaleString()}</span>
                </li>
                <li className="flex flex-col">
                  <span className="text-sm font-medium">Total Cost</span>
                  <span className="text-sm text-[var(--color-text-secondary)]">{`$${currentSession.totalCost.toFixed(4)}`}</span>
                </li>
                <li className="flex flex-col">
                  <span className="text-sm font-medium">Created</span>
                  <span className="text-sm text-[var(--color-text-secondary)]">{new Date(currentSession.createdAt).toLocaleString()}</span>
                </li>
                {currentSession.lastActivityAt && (
                  <li className="flex flex-col">
                    <span className="text-sm font-medium">Last Activity</span>
                    <span className="text-sm text-[var(--color-text-secondary)]">{new Date(currentSession.lastActivityAt).toLocaleString()}</span>
                  </li>
                )}
              </ul>

              {agentUsesHarness && (
                <>
                  <hr className="border-[var(--color-border-default)] my-6" />
                  <h3 className="text-sm font-semibold mb-1">Harness todos</h3>
                  {harnessTodos.length === 0 ? (
                    <span className="text-xs text-[var(--color-text-secondary)]">
                      No todos yet. Multi-step requests create todos that persist across turns.
                    </span>
                  ) : (
                    <ul className="space-y-1">
                      {harnessTodos.map((todo) => (
                        <li key={todo.id} className="py-1">
                          <span
                            className={`text-[13px] ${todo.isComplete ? 'line-through text-[var(--color-text-disabled)]' : ''}`}
                          >
                            {`${todo.isComplete ? '✓' : '○'} ${todo.title}`}
                          </span>
                          {todo.description && (
                            <span className="block text-xs text-[var(--color-text-secondary)]">{todo.description}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              <hr className="border-[var(--color-border-default)] my-6" />
              <h3 className="text-sm font-semibold mb-1">Tool activity</h3>
              {toolActivity.length === 0 ? (
                <span className="text-xs text-[var(--color-text-secondary)]">
                  No tool calls recorded in this session yet.
                </span>
              ) : (
                <ul className="space-y-1">
                  {toolActivity.map((entry) => (
                    <li key={entry.key} className="flex items-center justify-between py-1">
                      <div className="pr-2 min-w-0">
                        <span className="text-[13px] break-all">{entry.name}</span>
                        {entry.timestamp && (
                          <span className="block text-[11px] text-[var(--color-text-secondary)]">
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                      <Chip
                        className="text-xs"
                        variant="outlined"
                        color={entry.status === 'completed' ? 'green' : entry.status === 'failed' ? 'red' : 'default'}
                        label={entry.status === 'completed' ? 'Completed' : entry.status === 'failed' ? 'Failed' : 'Called'}
                      />
                    </li>
                  ))}
                </ul>
              )}

              <hr className="border-[var(--color-border-default)] my-6" />
              <h3 id="context-summaries" className="text-sm font-semibold mb-1">Chat compaction</h3>
              <ContextSummaryList sessionId={currentSession.id} />

              <hr className="border-[var(--color-border-default)] my-6" />
              <SessionMemoryList sessionId={currentSession.id} />

              <hr className="border-[var(--color-border-default)] my-6" />
              <FileUpload
                sessionId={currentSession.id}
                onUploaded={() => setFilesRefreshKey((k) => k + 1)}
              />
              <FileList sessionId={currentSession.id} refreshKey={filesRefreshKey} />
            </div>
          </div>
        </div>

        {parsedParameters && (
          <div
            className="flex flex-col flex-shrink-0 self-stretch min-h-0 overflow-hidden border-l border-[var(--color-border-default)] bg-[var(--color-bg-subtle)]"
            style={{ width: 'min(320px, 30vw)', minWidth: 260 }}
          >
            <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0 border-b border-[var(--color-border-default)]">
              <TuneOutlinedIcon className="text-[var(--color-text-secondary)]" size={18} />
              <span className="text-xs font-semibold text-[var(--color-text-secondary)]">Parameters</span>
            </div>
            <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-3 min-h-0">
              {parsedParameters.map(([key, value]) => {
                const displayLabel = parameterLabelsByKey[key] ?? key;
                const valueStr =
                  typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
                return (
                  <Chip
                    key={key}
                    variant="outlined"
                    className="text-xs w-full justify-start whitespace-normal text-left min-h-[32px] h-auto py-1.5"
                    label={`${displayLabel}: ${valueStr}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};
